import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import { PdfEntrepriseHeaderIcon } from "src/core/components/pdf/icons/PdfEntrepriseHeaderIcon";
import { PdfPersonHeaderIcon } from "src/core/components/pdf/icons/PdfPersonHeaderIcon";
import { CustomRiskLevel } from "src/core/constants/risk";
import { DossierComplet } from "src/core/hooks/folders-list/folder-risk-statistics/types";

const styles = StyleSheet.create({
  table: {
    marginBottom: 5,
    borderWidth: 1,
    borderColor: "#E6E8ED",
    borderRadius: 12,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F5F6F9",
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E6E8ED",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    alignItems: "center",
  },
  tableHeaderCell: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#0A1A53",
    textTransform: "uppercase",
  },

  // Raison sociale
  tableCellRaisonSocialeWrapper: {
    width: "40%",
    flexDirection: "row",
    alignItems: "center",
  },
  tableHeaderCellRaisonSociale: {
    width: "40%",
    fontSize: 9,
    fontWeight: "bold",
    color: "#0A1A53",
    textTransform: "uppercase",
  },
  tableCellRaisonSociale: {
    fontSize: 9,
    color: "#0A1A53",
  },
  folderNameContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  folderIcon: {
    marginRight: 6,
  },

  // EC en charge
  tableHeaderCellEC: {
    width: "25%",
    fontSize: 9,
    fontWeight: "bold",
    color: "#0A1A53",
    textTransform: "uppercase",
  },
  tableCellEC: {
    width: "25%",
    fontSize: 9,
    color: "#0A1A53",
  },

  // Vigilance
  tableHeaderCellRisque: {
    width: "15%",
    fontSize: 9,
    fontWeight: "bold",
    color: "#0A1A53",
    textTransform: "uppercase",
  },
  riskCellHigh: {
    width: "15%",
    fontSize: 9,
    color: "#FE6447",
  },
  riskCellMedium: {
    width: "15%",
    fontSize: 9,
    color: "#F3AC60",
  },
  riskCellLow: {
    width: "15%",
    fontSize: 9,
    color: "#00D6A8",
  },

  // Dernière validation
  tableHeaderCellValidation: {
    width: "20%",
    fontSize: 9,
    fontWeight: "bold",
    color: "#0A1A53",
    textTransform: "uppercase",
  },
  tableCellValidation: {
    width: "20%",
    fontSize: 9,
    color: "#0A1A53",
  },

  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E6E8ED",
    alignItems: "center",
  },
  tableRowLast: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: "center",
    borderBottomWidth: 0,
  },
  tableCell: {
    fontSize: 9,
    color: "#0A1A53",
  },
});

const getRiskColor = (riskLevel: CustomRiskLevel): "high" | "medium" | "low" => {
  if (riskLevel === CustomRiskLevel.HIGH) {
    return "high";
  }
  if (riskLevel === CustomRiskLevel.MEDIUM || riskLevel === CustomRiskLevel.NORMAL) {
    return "medium";
  }
  if (riskLevel === CustomRiskLevel.LOW) {
    return "low";
  }
  // Par défaut, on traite comme medium pour les cas non définis
  return "medium";
};

const getRiskLabel = (riskLevel: CustomRiskLevel, t: (key: string) => string): string => {
  switch (riskLevel) {
    case CustomRiskLevel.HIGH:
      return t("folders.list.columns.riskLevels.high");
    case CustomRiskLevel.MEDIUM:
    case CustomRiskLevel.NORMAL:
      return t("folders.list.columns.riskLevels.medium");
    case CustomRiskLevel.LOW:
      return t("folders.list.columns.riskLevels.low");
    default:
      return t("folders.list.columns.riskLevels.medium");
  }
};

type FoldersTableProps = {
  folders: DossierComplet[];
};

export const FoldersTable = ({ folders }: FoldersTableProps) => {
  const { t } = useTranslation();

  if (folders.length === 0) return null;

  return (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        <Text style={styles.tableHeaderCellRaisonSociale}>
          {t("pdf.validatedFolders.sections.foldersTable.raisonSociale")}
        </Text>
        <Text style={styles.tableHeaderCellEC}>
          {t("pdf.validatedFolders.sections.foldersTable.expertComptable")}
        </Text>
        <Text style={styles.tableHeaderCellRisque}>
          {t("pdf.validatedFolders.sections.foldersTable.risk")}
        </Text>
        <Text style={styles.tableHeaderCellValidation}>
          {t("pdf.validatedFolders.sections.foldersTable.revalidationDate")}
        </Text>
      </View>
      {folders.map((folder, index) => {
        const isLastRow = index === folders.length - 1;
        const riskColor = getRiskColor(folder.globalRisk);
        const riskLabel = getRiskLabel(folder.globalRisk, t);

        return (
          <View key={folder.id} style={isLastRow ? styles.tableRowLast : styles.tableRow}>
            <View style={styles.tableCellRaisonSocialeWrapper}>
              {folder.isPerson ? (
                <PdfPersonHeaderIcon size={12} style={styles.folderIcon} />
              ) : (
                <PdfEntrepriseHeaderIcon size={12} style={styles.folderIcon} />
              )}
              <Text style={styles.tableCellRaisonSociale}>{folder.name || "-"}</Text>
            </View>

            <Text style={styles.tableCellEC}>{folder.inChargeOf || "-"}</Text>
            <Text
              style={[
                { fontWeight: "bold" },
                riskColor === "high"
                  ? styles.riskCellHigh
                  : riskColor === "medium"
                  ? styles.riskCellMedium
                  : styles.riskCellLow,
              ]}
            >
              {riskLabel}
            </Text>
            <Text style={styles.tableCellValidation}>{folder.lastModification || "-"}</Text>
          </View>
        );
      })}
    </View>
  );
};
